import Vue from 'vue'
import Vuex from 'vuex'
import jwtDecode from 'jwt-decode'
import ApiGtag, { Category } from '@/services/api-gtag'
import { AxiosResponse } from 'axios'
import router from '@/router'
import {
  AuthenticationToken,
  Credentials,
  Modal,
  Route,
  User,
  Product,
  EditorialProject
} from '@/models'
import { ActionTypes } from './action-types'
import { MutationTypes } from './mutation-types'
import ApiClient from '@/services/api-client'

Vue.use(Vuex)

const AUTH_TOKEN = 'authToken'
const USER_INFO = 'userInfo'
const USER_LAST_ROUTES = 'routes'
const USER_FAVORITE_COURSES = 'favoriteCourses'
const SHOW_LABS = 'showLabs'
const defaultModal = {
  show: false,
  title: 'Título',
  message: 'msg',
  type: 'info'
}

export const state = {
  user: JSON.parse(localStorage.getItem(USER_INFO) || '{}'),
  token: JSON.parse(localStorage.getItem(AUTH_TOKEN) || '{}'),
  routes: JSON.parse(localStorage.getItem(USER_LAST_ROUTES) || '[]'),
  favoriteCourses: JSON.parse(
    localStorage.getItem(USER_FAVORITE_COURSES) || '[]'
  ),
  activeProject: {} as EditorialProject,
  activeProduct: {} as Product,
  activeLang: '',
  isLoading: false,
  modal: defaultModal,
  errorModal: { show: false, title: '', message: '', color: 'danger', copyable: true },
  showLabs: localStorage.getItem(SHOW_LABS) === 'true' || false
}
export type State = typeof state

export default new Vuex.Store({
  state: state,
  getters: {
    userInfo: (state) => {
      return state.user
    },
    isUserAuthenticated: (state) => {
      if (state.token.accessToken) {
        const jwtToken: { sub: string; iss: string; exp: number } = jwtDecode(
          state.token.accessToken
        )
        if (jwtToken.exp * 1000 >= Date.now()) {
          return true
        }
      }
      return false
    },
    isAdmin: (state) => {
      return (
        state.user &&
        state.user.groups &&
        state.user.groups.includes('admin') === true
      )
    },
    isEditor: (state) => {
      return (
        state.user &&
        state.user.groups &&
        state.user.groups.includes('editor') === true
      )
    },
    lastRoutes: (state) => {
      return state.routes
    },
    showModal: (state) => {
      return state.modal.show
    },
    isLoading: (state) => {
      return state.isLoading
    },
    modal: (state) => {
      return state.modal
    },
    errorModal: (state) => {
      return state.errorModal
    }
  },
  mutations: {
    [MutationTypes.SET_USER]: (state: State, payload: User) => {
      state.user = payload
    },
    [MutationTypes.SET_TOKEN]: (state: State, token) => {
      localStorage.setItem(AUTH_TOKEN, JSON.stringify(token))
      state.token = token
    },
    [MutationTypes.SET_ROUTES]: (state: State, routes) => {
      state.routes = routes
    },
    [MutationTypes.SET_LOADING]: (state: State, set) => {
      state.isLoading = set
    },
    [MutationTypes.SET_MODAL]: (state: State, modal: Modal) => {
      state.modal = modal
    },
    [MutationTypes.SET_ERROR_MODAL]: (
      state: State,
      payload: { show: boolean; title: string; message: string; color: string; copyable: boolean }
    ) => {
      state.errorModal = payload
    },
    [MutationTypes.SET_FAVORITE_COURSES]: (state: State, favorite) => {
      state.favoriteCourses = favorite
    },
    [MutationTypes.SET_ACTIVE_PROJECT]: (state: State, project: EditorialProject) => {
      state.activeProject = project
    },
    [MutationTypes.SET_ACTIVE_PRODUCT]: (state: State, product: Product) => {
      state.activeProduct = product
    },
    [MutationTypes.SET_ACTIVE_LANG]: (state: State, lang) => {
      state.activeLang = lang
    },
    [MutationTypes.SET_SHOW_LABS]: (state: State) => {
      state.showLabs = !state.showLabs
      localStorage.setItem('showLabs', JSON.stringify(state.showLabs))
    }
  },
  actions: {
    [ActionTypes.AUTH_LOGIN]({ commit, dispatch }, credentials: Credentials) {
      commit(MutationTypes.SET_MODAL, defaultModal)
      commit(MutationTypes.SET_LOADING, true)
      ApiClient.login(credentials)
        .then((response) => {
          const authToken: AuthenticationToken = response
          commit(MutationTypes.SET_TOKEN, authToken)
          commit(MutationTypes.SET_LOADING, false)
          return credentials.username
        })
        .then((username) => {
          dispatch(ActionTypes.USER_REQUEST, username)
          const newRoute: string =
            router.currentRoute.params && router.currentRoute.params.to
          router.push({ path: newRoute || '/' })
        })
        .catch((err) => {
          console.info('error login', err.response.status)
          let modal: Modal = { show: false, title: '', message: '', type: '' }
          commit(MutationTypes.SET_LOADING, false)

          if (err.response.status === 401) {
            modal = {
              show: true,
              title: 'Alerta',
              message: 'Usuario o contraseña erróneos',
              type: 'danger'
            }
          } else if (err.response.status === 403) {
            modal = {
              show: true,
              title: 'Alerta',
              message: 'No tiene privilegios para acceder',
              type: 'danger'
            }
          } else if (err.response.status >= 500 || err.response.status < 599) {
            modal = {
              show: true,
              title: 'Alerta',
              message: 'Error del servidor, contacte con soporte',
              type: 'danger'
            }
          } else {
            modal = {
              show: true,
              title: 'Alerta',
              message: 'Error desconocido, contacte con soporte',
              type: 'danger'
            }
          }
          commit(MutationTypes.SET_MODAL, modal)
        })
    },
    [ActionTypes.USER_REQUEST]({ commit }) {
      ApiClient.getUserInfo()
        .then((user: User) => {
          const userInfo = {
            ...user,
            displayUsername: user.username.replace('IdentityProvider_', '')
          }
          localStorage.setItem(USER_INFO, JSON.stringify(userInfo))
          commit(MutationTypes.SET_USER, userInfo)
          // set user and language GTAG values
          ApiGtag.setConfig(state.activeLang, state.user.username)
          // event login fot GTAG
          ApiGtag.sendEvent(Category.User, 'Login', userInfo.username)
        })
        .catch((error: AxiosResponse) => {
          const user: User = {
            displayUsername: 'displayUsername',
            username: 'Username',
            name: 'Nombre',
            nickname: 'Nickname',
            email: 'mail@grupo-sm.com',
            groups: ['user']
          }
          localStorage.setItem(USER_INFO, JSON.stringify(user))
          const modal: Modal = {
            show: true,
            title: 'Alerta',
            message: `Error al obtener datos de usuario ${error.data.message}`,
            type: 'danger'
          }
          commit(MutationTypes.SET_MODAL, modal)
        })
    },
    [ActionTypes.AUTH_LOGOUT]({ commit }) {
      ApiClient.logout()
      localStorage.removeItem(AUTH_TOKEN)
      localStorage.removeItem(USER_INFO)
      commit(MutationTypes.SET_TOKEN, '')
      commit(MutationTypes.SET_USER, {})
      router.push('/login')
    },
    [ActionTypes.STORE_ROUTES]({ commit }, routes: Route[]) {
      const routesTo5 = routes.slice(0, 10)
      localStorage.setItem(USER_LAST_ROUTES, JSON.stringify(routesTo5))
      commit(MutationTypes.SET_ROUTES, routesTo5)
    },
    [ActionTypes.STORE_FAVORITE_COURSES]({ commit }, product: Product) {
      const favArray = state.favoriteCourses

      if (!favArray.length) {
        favArray.push(product)
      } else {
        const i = favArray.findIndex((el: any) => el.id === product.id)
        if (i < 0) {
          favArray.push(product)
          ApiGtag.sendEvent(
            Category.Course,
            'Add favoriteCourses',
            `${product.id}/${product.title}`
          )
        } else {
          favArray.splice(i, 1)
          ApiGtag.sendEvent(
            Category.Course,
            'Remove favoriteCourses',
            `${product.id}/${product.title}`
          )
        }
      }

      favArray.sort((a: { title: string }, b: { title: string }) =>
        a.title
          .replace(/\s+/, '')
          .toLowerCase()
          .localeCompare(b.title.replace(/\s+/, '').toLowerCase())
      )

      localStorage.setItem(USER_FAVORITE_COURSES, JSON.stringify(favArray))
      commit(MutationTypes.SET_FAVORITE_COURSES, favArray)
    }
  },
  modules: {}
})
